import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { BILLING_PLANS_PATH } from "../../../routes/paths";
import type { AccessLapse } from "../../billing/accessLapse";
import type { TermCountdown } from "../../billing/termCountdown";
import lapsedArtwork from "../../../assets/svg/Dsahboard/subscription_end_banner.png";
import lapsedPhoneArtwork from "../../../assets/svg/Dsahboard/mobile_sidebar_subscription_end_banner.webp";
import trialArtwork from "../../../assets/svg/Dsahboard/banner.png";
import trialPhoneArtwork from "../../../assets/svg/Dsahboard/mo-banner-image.webp";

export interface UpgradeBannerProps {
  /**
   * What has just run out, where it can be told — the same fact every greyed
   * create button on this screen is answering with. See `accessLapse`.
   *
   * The strip is drawn whatever this says: all three readers have no plan and
   * all three are being offered the same one. What changes is only whether
   * they are being asked to keep something they still have, or told what they
   * have lost — and a practice that has been buying terms for a year must not
   * be told it was a free trial that ended.
   */
  ended?: AccessLapse;
  /**
   * The last week of a term that is still running, or `null` on every other
   * day — see `termCountdown`.
   *
   * It outranks `ended`, and the two should never both be set anyway: a term
   * counting down is a workspace whose door is open, and `ended` is only ever
   * non-null once it has shut. Carried as the countdown rather than as a bare
   * number so the strip cannot round, pluralise or decide urgency for itself;
   * all three are settled before it gets here.
   */
  ending?: TermCountdown | null;
}

/**
 * The strip across the top of the dashboard: what the workspace is not on yet,
 * and the way onto it.
 *
 * Drawn for a workspace that has no plan, and for one whose term is inside its
 * last week — see `DashboardPage`, which decides both. An offer to upgrade is a
 * claim about somebody's account, so it is not put to someone already paying,
 * and not put while the billing read has yet to answer. Same rule as the rail's
 * "Subscribe now" line.
 *
 * The countdown is the exception that proves it, and the only thing here ever
 * said to a subscriber: it claims nothing about their account they do not
 * already know, and the alternative is a term lapsing under a practice with no
 * warning at all — nothing renews itself, so there is no second chance after
 * the fact. See `termCountdown`.
 *
 * Two ways of saying it, on either side of the trial. "Keep going with Pro" is
 * an offer to somebody who still has the product open — a workspace on its
 * fortnight, or one that never took a trial at all. Past the end date it would
 * be describing something that is no longer true, so the strip says plainly
 * what happened and what buying gets back. Same reasoning as the locked
 * buttons' two answers in `gateReason`.
 *
 * Two colours, decided by whether money has ever changed hands: purple for a
 * paid term that has lapsed or is about to, and the original blue for the
 * trial and the plain offer. See `SKINS`, which is where that is written down
 * and why. Every colour on the strip — the fallback fill, the button's label,
 * the focus ring's offset — is sampled from whichever artwork is behind it, so
 * it holds together before the image is decoded and if it never arrives.
 *
 * The artwork is a background rather than an `<img>`: it carries no information
 * the two lines over it do not already say, and as a background it can be
 * cropped to whatever width the screen gives without the words moving with it.
 *
 * On a phone the pill stays but stops sitting beside the heading: the two lines
 * and the button stack down the left, which is the one arrangement that leaves
 * both of them full size at that width. A visible button is worth the row it
 * costs — it is what makes the strip read as an offer rather than as a notice
 * that happens to be tappable.
 */
/**
 * The three fixed things the strip can be saying, and the one place each of
 * them is written. The fourth is the countdown, which is built in the component
 * because its heading carries a number.
 *
 * Two of these are endings and read alike on purpose: what a practice between
 * terms and one past its fortnight both need is the way back in, and the only
 * thing that differs is which fact is being named. The third is the ordinary
 * offer — a trial still running, or a workspace that has never bought — where
 * nothing has been lost and the strip is an invitation rather than a notice.
 *
 * `lead` is the green line, `line` the white one under it, and `cta` the
 * button; all three are chosen together, so the strip cannot end up offering
 * to "keep" something it has just said is over.
 */
const WORDING: Record<
  "trial" | "plan" | "none",
  {
    lead: string;
    line: string;
    cta: string;
  }
> = {
  trial: {
    // "Trial ended" on its own is `TRIAL_ENDED_HEADING`, the locked buttons'
    // dialog — and that dialog is opened from this very screen, so the two
    // would be the same three words in two places at once. The word the gate
    // leaves out is the one that keeps them apart.
    lead: "Free trial ended",
    line: "Your Pro access has paused",
    cta: "Subscribe now",
  },
  plan: {
    lead: "Subscription ended",
    line: "Your Pro access has paused",
    cta: "Subscribe now",
  },
  none: {
    lead: "Keep going with Pro",
    line: "Switch to paid and stay uninterrupted.",
    cta: "Keep pro active",
  },
};

/**
 * The two looks the strip has, and which of the four things it says wears
 * each.
 *
 * Purple is the paid term — one that has lapsed, and one about to. Blue is the
 * trial and the plain offer, which is what the strip has always been. The
 * split is the design's and it is doing work: a practice that has been paying
 * is not being sold the product for the first time, and dressing the end of a
 * term in the same blue as the signup offer reads as marketing to somebody who
 * has already bought. Whether money has changed hands is the one thing that
 * separates the two groups, so it is what separates the two skins.
 *
 * Each colour is written out as a whole class rather than interpolated from a
 * hex, because Tailwind reads this file as text: a class built at runtime is a
 * class that never reaches the stylesheet.
 *
 * Both carry two pieces of artwork, for the shapes they fill rather than for
 * the news they carry — a strip eight times longer than it is tall on a wide
 * screen, and something near enough square on a phone. Handing the wide one to
 * a phone showed a 328px slice of it, and the only slice that cleared the
 * heading was the flat colour at the far right, which is to say no artwork at
 * all. The fill under each is sampled from its own image: what the words sit
 * on for the moment before it is decoded, and if it never loads.
 */
const SKINS = {
  /** A term that has run out, or is about to. */
  lapsed: {
    art: lapsedArtwork,
    phoneArt: lapsedPhoneArtwork,
    fill: "#613D97",
    // The phone's artwork pinned to the corner it was drawn for, so the sliver
    // `cover` takes comes off the flat purple at the left rather than the
    // rings. The wide one is centred, which is where its own proportions put
    // the feathers clear of the heading.
    position: "bg-right-bottom sm:bg-center",
    label: "text-[#613D97]",
    ring: "focus-visible:ring-offset-[#613D97]",
  },
  /** The fortnight, either side of its end, and the workspace that has never
   *  bought anything. */
  trial: {
    art: trialArtwork,
    phoneArt: trialPhoneArtwork,
    fill: "#3451D9",
    // `bg-right` rather than the corner: this phone artwork carries its swirl
    // at the right edge rather than the bottom of it.
    position: "bg-right sm:bg-center",
    // A shade off the fill, which is the design's own: the label sits on white
    // and the fill is tuned to sit under white text.
    label: "text-[#3F51A5]",
    ring: "focus-visible:ring-offset-[#3451D9]",
  },
} as const;

const UpgradeBanner: React.FC<UpgradeBannerProps> = ({
  ended = null,
  ending = null,
}) => {
  // The countdown first, because it is the only one of the four that is put to
  // a workspace still inside its term — and the only one whose heading carries
  // a number, which is why it is built here rather than sitting in `WORDING`
  // with the three that are fixed.
  const words = ending
    ? {
        lead: `Subscription ending ${ending.label}`,
        line: "Your Pro subscription is ending soon. Renew now to keep your Pro access uninterrupted.",
        cta: "Renew now",
      }
    : WORDING[ended ?? "none"];

  // Purple for a paid term, blue for everything else — see `SKINS`. A trial
  // that has ended is still a trial, so it keeps the blue it ran in.
  const skin = ending || ended === "plan" ? SKINS.lapsed : SKINS.trial;

  return (
    <section
      // The two images handed over as custom properties so the breakpoint
      // stays in the class list with every other one, rather than in a size
      // question asked in JavaScript. Which pair they are is `skin` — see
      // `SKINS`.
      style={
        {
          "--banner-art": `url(${skin.phoneArt})`,
          "--banner-art-wide": `url(${skin.art})`,
          backgroundColor: skin.fill,
        } as React.CSSProperties
      }
      className={`relative overflow-hidden rounded-lg bg-[image:var(--banner-art)] sm:bg-[image:var(--banner-art-wide)] bg-cover ${skin.position} flex flex-col items-start justify-center gap-6 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-4 min-h-[136px] sm:min-h-[140px] 3xl:min-h-[170px] py-5 pl-5 pr-5 sm:pl-10 sm:pr-8 lg:pl-24 xl:pl-36 3xl:pl-48`}
    >
      <div>
        {/* Two lines of one sentence, so they are one heading with a break in it
          rather than a heading and a subtitle — the second line is the half
          that means something. */}
        {/* The countdown's second line is a sentence rather than the four or
          five words the other three use, so it is given a measure to wrap
          inside: left to the strip's full width it would run past the button
          on a wide screen and read as a paragraph that had lost its box. The
          cap is only taken from `sm`, since a phone is already narrower than
          it. */}
        <h2
          className={`text-[17px] sm:text-[26px] 3xl:text-[28px] font-semibold leading-snug${
            ending ? " sm:max-w-[34rem]" : ""
          }`}
        >
          {/* The green the design puts on the news, and the only place in the
            app it is used — it exists to lift that line off whichever of the
            two colours is behind it, and it reads on both. */}
          <span className="block text-[#52FF89]">{words.lead}</span>
          {/* A size down for the countdown's line only. The other three say
            their half of a heading in a handful of words and carry it at the
            same weight as the first; a full sentence set that large stops
            being a second line and starts being a wall. */}
          <span
            className={`block text-white${
              ending ? " text-[14px] sm:text-[17px] 3xl:text-[19px]" : ""
            }`}
          >
            {words.line}
          </span>
        </h2>
      </div>

      {/* A link rather than a button that navigates, for the reason the setup
        checklist gives: it is a screen of the app, so a middle click opens it
        in a tab and the status bar says where it leads.

        The price list, not the Billings tab's default view. Every one of the
        four things this strip says ends in "buy something" — a term to come
        back from, a term to follow this one, or the first one — and the tab
        left to itself opens on the plan the workspace is already on. For the
        two lapsed states that is an expired record, and for the countdown it
        is the term they are trying to replace: all three cost a click to get
        off. See `BILLING_PLANS_PATH`. */}
      <Link
        to={BILLING_PLANS_PATH}
        className={`rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 ${skin.ring}`}
      >
        {/* Solid white, with the label in the colour it is standing on — the
          one thing on the strip that is pressed, so it is the one thing filled
          in. Smaller on a phone, where it is under the heading rather than
          beside it and has the width of the strip to itself. */}
        <span
          className={`inline-flex flex-shrink-0 items-center gap-2 rounded-md bg-white px-3.5 py-2 sm:px-4 sm:py-2.5 3xl:px-5 3xl:py-3 text-[13px] sm:text-[14px] 3xl:text-[16px] font-medium ${skin.label} transition-colors hover:bg-white/90`}
        >
          {words.cta}
          <ArrowRight aria-hidden className="w-4 h-4" />
        </span>
      </Link>
    </section>
  );
};

export default UpgradeBanner;
